import { isSameDay, parseISO } from 'date-fns'
import { formatInTimeZone, utcToZonedTime } from 'date-fns-tz'
import capitalize from 'lodash/capitalize'
import { observer } from 'mobx-react'
import { FC, useEffect, useRef } from 'react'

import { NotificationType } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { MiddleEllipsis, timeAgoFromToday, useIsVisible } from '@avantstay/backoffice-core'
import { IconClock, IconComment, IconTool } from '@avantstay/backoffice-vectors'

import { mountRouteUrl, openLink, ROUTES } from '@routes'

import {
  getNotificationCategory,
  NotificationCategory,
  NotificationProps,
} from '../../OperationsNotification.types'
import { operationsNotificationStore } from '../../OperationsNotificationStore'
import * as S from './NotificationCard.styles'

const TIMEZONE = 'America/Los_Angeles'

const categoryIconMap: Record<NotificationCategory, React.ReactNode> = {
  [NotificationCategory.mention]: <IconComment width={16} height={16} />,
  [NotificationCategory.case]: <IconTool />,
  [NotificationCategory.other]: <IconClock width={16} height={16} />,
}

const _NotificationCard: FC<{ notification: NotificationProps }> = ({ notification }) => {
  const { addNotificationToReadQueue } = operationsNotificationStore
  const category = getNotificationCategory(notification.notificationType)

  const resolveNotificationTitle = () => {
    switch (notification.notificationType) {
      case NotificationType.isgiIssueCreated:
        return 'ISGI in your market'
      case NotificationType.urgentCaseCreated:
        return `${capitalize(notification.casePriority)} priority case created`
      case NotificationType.caseAssignedToMtVendor:
      case NotificationType.caseAssignedToVendor:
        return 'New case assigned to you'
      case NotificationType.userTaggedInComment:
        return `${notification.commentAuthorName} tagged you in a comment`
      case NotificationType.mtAvailabilityCheckReminder:
        return 'Reminder'
      default:
        return notification.caseSubject
    }
  }
  const parseNotificationDate = () => {
    const dateString = notification.createdAt.endsWith('Z')
      ? notification.createdAt
      : `${notification.createdAt}Z`
    const date = parseISO(dateString)
    const now = new Date()

    const dateInTimezone = utcToZonedTime(date, TIMEZONE)
    const nowInTimezone = utcToZonedTime(now, TIMEZONE)

    const isToday = isSameDay(dateInTimezone, nowInTimezone)
    if (isToday) return timeAgoFromToday(dateString)
    return formatInTimeZone(date, TIMEZONE, 'hh:mm a')
  }

  const cardContainerRef = useRef<HTMLDivElement | null>(null)

  const isCardVisible = useIsVisible({ containerRef: cardContainerRef })

  useEffect(() => {
    if (isCardVisible && !notification.readAt) {
      addNotificationToReadQueue(notification.id)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCardVisible, notification.readAt])

  const shouldNavigateToCaseDetails = [
    NotificationType.urgentCaseCreated,
    NotificationType.isgiIssueCreated,
    NotificationType.caseAssignedToMtVendor,
    NotificationType.caseAssignedToVendor,
    NotificationType.userTaggedInComment,
  ].includes(notification.notificationType)

  const handleOnClick = () => {
    if (shouldNavigateToCaseDetails) {
      openLink(mountRouteUrl(ROUTES.operationCases.path, undefined, { case: notification.workOrderId }))
    }
  }

  return (
    <S.CardContainer
      ref={cardContainerRef}
      onClick={handleOnClick}
      hasLink={shouldNavigateToCaseDetails}
      category={category}
    >
      <S.CardHeader>
        <S.IconContainer>{categoryIconMap[category]}</S.IconContainer>

        <S.TitleText>{resolveNotificationTitle()}</S.TitleText>
        <S.DateText>{parseNotificationDate()}</S.DateText>
        {notification.readAt ? <div /> : <S.UnreadDot />}
      </S.CardHeader>
      <S.Subject>{notification.caseSubject}</S.Subject>
      {notification.homeName && (
        <S.HomeNameWrapper>
          <MiddleEllipsis data={notification.homeName} tailLength={10} />
        </S.HomeNameWrapper>
      )}
    </S.CardContainer>
  )
}

export const NotificationCard = observer(_NotificationCard)
